import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/internal/operators/first';

import { IMCMapView } from '../../classes/mcMapView';
import { ESRI } from '../../esri-loader-util';
import { InputConverter } from '../../inputConverter';
import { GisToolsService } from '../../services/gis-tools.service';
import { IAuthenticationService } from '../../services/interfaces/IAuthenticationService';
import { IUserSettingsService } from '../../services/interfaces/IUserSettingsService';
import { ICounty, IDistrict, IUserSetting } from '../../types';
import { BaseComponent } from '../base.component';

import UserSettingsConfiguration, { UserSettingsConfigurationArrayConverter } from './user-settings.config';

let Extent: __esri.ExtentConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-user-settings',
    styleUrls: ['../sharedStyles/base.scss', './user-settings.component.scss'],
    templateUrl: './user-settings.component.html'
})
export class UserSettingsComponent extends BaseComponent {
    @Input()
    @InputConverter(UserSettingsConfigurationArrayConverter)
    userSettingsConfigs: UserSettingsConfiguration[];

    userSettings: IUserSettings = {
        districts: { value: [] },
        counties: { value: [] }
    };
    userSettingsSubscriber: Subscription;

    private _active: boolean = false;
    @InputConverter()
    @Input() set active(value: boolean) {
        this._active = value;
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }

    @Input() appName: string;

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();
    @Output() readonly settingsChange: EventEmitter<IUserSettings> = new EventEmitter();

    mapView: NonNullable<__esri.MapView>;
    mapRef: IMCMapView;
    userSubscriber: Subscription;
    userEmail: string;

    allDistricts: IDistrict[] = [];
    allCounties: ICounty[] = [];

    constructor(@Inject('authenticationService') private readonly authService: IAuthenticationService,
                @Inject('UserSettingsService') public UserSettingsService: IUserSettingsService,
                private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly gisToolsService: GisToolsService,
                _vcr: ViewContainerRef) {
        super(_vcr, true);
        this.registerWithMcScope();
        // Defaults
        if (!this.userSettingsConfigs) {
            this.userSettingsConfigs = [];
            this.userSettingsConfigs.push(new UserSettingsConfiguration({
                name: 'districts',
                label: 'Districts',
                type: 'districts',
                isSelect: true
            }));
            this.userSettingsConfigs.push(new UserSettingsConfiguration({
                name: 'counties',
                label: 'Default Counties',
                type: 'counties',
                isSelect: true
            }));
            this.userSettingsConfigs.push(new UserSettingsConfiguration({
                name: 'extent',
                label: 'Extent',
                type: 'extent',
                items: [
                    { name: 'default', label: 'Default Extent', value: null }
                ]
            }));
            /*this.userSettingsConfigs.push(new UserSettingsConfiguration({
                name: 'map-options',
                label: 'Map Options',
                type: 'check-list',
                items: [
                    { name: 'promptBeforeReplace', label: 'Prompt before replacing map', value: false },
                    { name: 'preserveExtent', label: 'Preserve extent on map change', value: false },
                    { name: 'zoomAfterFiltering', label: 'Zoom to layer after filtering', value: false }//,
                    { name: 'displayScalebar', label: 'Display scalebar on map', value: true }
                ]
            }));*/
        }
    }

    private async activate() {
        Extent = await ESRI.Extent;

        if (!this.userSubscriber) {
            this.userSubscriber = this.authService.GetCurrentUser().subscribe(user => {
                if (user && user.id) {
                    this.userEmail = user.emailAddress;
                    this.loadUserSettings();
                }
            });
        }

        if (!this.userSettingsSubscriber) {
            this.userSettingsSubscriber = this.UserSettingsService.userSettingsChange.subscribe(setting => {
                if (setting.settingName === 'userSettings') {
                    this.convertSettingsOnLoad(setting.value);
                    this.userSettings = setting.value;
                    this.settingsChange.emit(setting.value);
                }
            });
        }
    }

    @Input()
    disabledSelects: { [key: string]: boolean } = {};

    @Input()
    convertSettingsForSave(userSettings: IUserSettings): any {
        const convertedSettings = {};
        for (const setting of Object.keys(userSettings)) {
            if (Array.isArray(userSettings[setting].value)) {
                convertedSettings[setting] = [ ...userSettings[setting].value ];
            } else {
                convertedSettings[setting] = { ...userSettings[setting] };
                delete convertedSettings[setting].name;
            }
            if (setting === 'extent' && convertedSettings[setting].default) {
                convertedSettings[setting] = { default: (<__esri.Extent>convertedSettings[setting].default).toJSON() };
            }
        }
        return convertedSettings;
    }

    @Input()
    convertSettingsOnLoad(userSettings: { [key: string]: any }) {
        for (const settingConfig of this.userSettingsConfigs) {
            const userSetting: any = {
                name: settingConfig.name,
                value: userSettings[settingConfig.name]
            };

            switch (settingConfig.type) {
                case 'check-list':
                case 'extent':
                    settingConfig.items.forEach(item => {
                        userSetting[item.name] = item.value;
                    });
                    break;

                case 'counties':
                case 'districts':
                    userSetting.value = userSetting.value || [];
                    break;

                /* tslint:disable-next-line:switch-default */
            }

            if (settingConfig.type === 'extent') {
                if (userSetting.value && userSetting.value.default) {
                    userSetting.default = Extent.fromJSON(userSetting.value.default);
                }

                delete userSetting.value;
            } else if (Array.isArray(userSetting.value)) {
                userSetting.value = [...(userSetting.value as any as any[])];
            } else {
                userSetting.value = { ...userSetting.value };
            }

            userSettings[settingConfig.name] = userSetting;
        }
    }

    /**
     * Make the tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.userSubscriber) {
            this.userSubscriber.unsubscribe();
            this.userSubscriber = undefined;
        }
        if (this.userSettingsSubscriber) {
            this.userSettingsSubscriber.unsubscribe();
            this.userSettingsSubscriber = undefined;
        }
        this._loaded = false;
    }

    private _loaded = false;
    async loadUserSettings() {
        if (!this._loaded) {
            this.mapRef = await this.setupMapViewRef(this.mapView);

            await new Promise<void>((resolve, _reject) => {
                this.UserSettingsService.GetSettingValue('userSettings', this.appName, this.userEmail).pipe(first()).subscribe(results => {
                    if (results) {
                        this.userSettings = results;
                        this.convertSettingsOnLoad(this.userSettings);
                    }
                    resolve();
                });
            });

            this.allDistricts = await this.gisToolsService.getAllDistricts('districtNumber,counties{countyCode}');
            const districtSetting = this.userSettingsConfigs.find(s => s.name === 'districts');
            districtSetting.items = this.getDistrictItems();

            this.allCounties = await this.gisToolsService.getAllCounties('countyCode,name,districtNumber');
            const countySetting = this.userSettingsConfigs.find(s => s.name === 'counties');

            if (this.userSettings.districts.value && this.userSettings.districts.value.length) {
                countySetting.items = this.getCountyItems(this.userSettings.districts.value.map(v => v.replace('district-', '')));
            } else {
                countySetting.items = this.getCountyItems();
            }

            this._loaded = true;
            this.settingsChange.emit(this.userSettings);
            this.changeDetectorRef.detectChanges();
        }
    }

    getDistrictItems() {
        return this.allDistricts.map(district => ({
            name: `district-${district.districtNumber}`,
            label: `District ${district.districtNumber}`,
            value: district.districtNumber
        }));
    }

    getCountyItems(selectedDistricts?: string[]): ISelectItem[] {
        let availableCounties = this.allCounties;
        if (selectedDistricts !== undefined && selectedDistricts.length) {
            const districts = this.allDistricts.filter(dis => selectedDistricts.includes(dis.districtNumber));
            availableCounties = availableCounties.filter(cty => districts.map(dis => dis.counties.map(c => c.countyCode)).flat().includes(cty.countyCode));
        }
        return availableCounties.map(county => ({
            name: `county-${county.countyCode}`,
            label: county.name,
            value: county.countyCode
        }));
    }

    isSelect(type: string) {
        return (type === 'select-list' || type === 'districts' || type === 'counties');
    }

    onSettingChange(_value: any, setting: string) {
        // If selecting a district, limit the counties
        if (setting === 'districts') {
            const countySetting = this.getCountySetting();
            if (countySetting) {
                countySetting.items = this.getCountyItemsForSelectedDistricts();
                this.userSettings.counties.value = this.userSettings.counties.value.filter((v: string) => countySetting.items.map(i => i.name).includes(v));
            }
        }
        this.settingsChange.emit(this.userSettings);
        this.changeDetectorRef.detectChanges();
    }

    getDistrictSetting() {
        return this.userSettingsConfigs.find(s => s.name === 'districts');
    }

    getCountySetting() {
        return this.userSettingsConfigs.find(s => s.name === 'counties');
    }

    getCountyItemsForSelectedDistricts() {
        let retVal: ISelectItem[] = null;
        const districtSetting = this.getDistrictSetting();
        const districtValues = this.userSettings[districtSetting.name].value as string[];
        const countySetting = this.getCountySetting();
        if (countySetting) {
            retVal = this.getCountyItems(districtValues.map(v => v.replace('district-', '')));
        }
        return retVal;
    }

    setExtent(settingName: string, itemName: string) {
        this.userSettings[settingName][itemName] = this.mapRef.extent;
        this.changeDetectorRef.detectChanges();
    }

    clearExtent(settingName: string) {
        this.userSettings[settingName].default = null;
        this.changeDetectorRef.detectChanges();
    }

    checkboxChange(settingName: string, checkItemName: string) {
        this.userSettings[settingName][checkItemName] = !this.userSettings[settingName][checkItemName];
        this.changeDetectorRef.detectChanges();
    }

    async saveSettings() {
        // If District selected but no counties, select all Counties for selected Districts
        const countySettingConfig = this.getCountySetting();
        const districtSettingConfig = this.getDistrictSetting();
        if (countySettingConfig && districtSettingConfig) {
            const countySetting = this.userSettings[countySettingConfig.name];
            const districtSetting = this.userSettings[districtSettingConfig.name];
            if (countySetting && (!countySetting.value || !countySetting.value.length) && districtSetting && districtSetting.value && districtSetting.value.length) {
                countySetting.value = this.getCountyItemsForSelectedDistricts().map(item => item.name);
            }
        }
        await this.UserSettingsService.SetSettingValue('userSettings', this.convertSettingsForSave(this.userSettings), this.userEmail, this.appName);
        this.activeChange.emit(false);
    }

    cancel() {
        this.activeChange.emit(false);
    }

    public getSettings() {
        return this.userSettings;
    }

    public getSetting(settingName: string) {
        return this.userSettings[settingName] || null;
    }
}

export interface IUserSettings { [key: string]: IUserSetting; }

interface ISelectItem {
    name: string;
    label: string;
    value: string;
}
